import { produce } from 'immer';

import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  repeat,
  nothing,
} from 'omni-ui';

import { CAMPAIGN_AUDIENCE_PERSONA_DEFAULT } from './persona-default-data.js';

import './persona-empathy-card.js';
import './persona-summary-card.js';
import './persona-text-card.js';
import './persona-word-cloud.js';

OmniStyleElement.register();

/**
 * @description Module that allows clients to present key information in a clean and summerized view that can be downloaded as an image to be used in a presentation
 * @extends OmniElement
 */
export class CampaignAudiencePersona extends OmniElement {
  /**
   * @property {Object} personaData - Object containing 4 entries of arrays representing the idividual coloums in the module, each containing objects with the data for each unique card
   * @property {Object} defaultData - defualt data/schema structure if nothing is returned from campaignAPI
   * @property {Function} save - save implementaion passed down by {@link CampaignStepDetail} that saves all the data from the module to our campaignAPI
   * @property {String} env - Application environment
   * @property {Object} context - Values passed in from wrapper/portal
   */
  static get properties() {
    return {
      personaData: { type: Object },
      context: { type: Object },
    };
  }

  /**
   * Audience Persona module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host {
          overflow: auto;
        }
        .omni div.box {
          height: 74.5rem;
          padding: 2.14rem 2.14rem 4.29rem 2.14rem;
          margin-bottom: 2.14rem;
          display: flex;
          flex-flow: column nowrap;
          gap: 2.86rem;
        }

        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .title-container {
          display: flex;
          flex-flow: column nowrap;
          margin: 0px 0.71rem;
        }

        .title-container p {
          color: var(--color-core-medium);
          line-height: 0.9;
        }

        .omni .title:not(:last-child) {
          margin-bottom: 0.36rem;
        }

        .header-container button {
          height: 2.29rem 0.43rem;
          margin: 0px 0.71rem;
          padding: 0 0.43rem;
          background-color: transparent;
          border: none;
        }
        omni-tooltip omni-icon:hover {
          cursor: pointer;
        }

        .grid-container {
          height: 100%;
          display: grid;
          grid-template-columns: 1fr 1.53fr 1fr 1.53fr;
          grid-template-areas: 'cl1 cl2 cl3 cl4';
          gap: 1.43rem;
          min-height: 0;
        }

        div.grid-column {
          display: flex;
          flex-flow: column nowrap;
          justify-content: stretch;
          gap: 1.43rem;
          min-height: 0;
        }

        div.cl1 {
          grid-area: cl1;
        }

        div.cl2 {
          grid-area: cl2;
        }

        div.cl3 {
          grid-area: cl3;
        }

        div.cl4 {
          grid-area: cl4;
        }
      `,
    ];
  }

  /**
   * Assigns {@link #saveData} data being passed down from {@link CampaignStepDetail} to {@link #personaData} or defaults it to {@link #CAMPAIGN_AUDIENCE_PERSONA_DEFAULT}.
   */
  connectedCallback() {
    super.connectedCallback();
    this.personaData =
      this.savedData?.personaData || CAMPAIGN_AUDIENCE_PERSONA_DEFAULT;
  }

  get exportImageRootElement() {
    return this.renderRoot.querySelector('div.grid-container');
  }

  /**
   * CampaignAudiencePersona event
   * @event CampaignAudiencePersona#omni-module-changed
   * @type {object}
   * @property {string} moduleId - The id of the module that changed
   * @property {Function} saveAction - The function to call when the step is saved
   */
  _assetChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitAttachments.bind(this),
        moduleType: 'audience persona viz',
      },
    });
  }

  /**
   * Submits and saves the data to CampaignAPI under the correct campaign and module.
   * Allows for the developer to structure the data as they see fit and stored in the APIs object under the key savedData
   */
  async _submitAttachments() {
    await this.save({ personaData: this.personaData });
  }

  /**
   * gets invoked when the @event persona-change is emitted by any of the individual cards. Utilizes produce from immer to make a copy/draft of the state {@link #personaData} and modifies the card located at the specific column and index with state from cardData
   * @param {Object} cardData - Object containing the individuals card data.
   * @param {String} column - The column the card is in
   * @param {Number} index - The index the card is located within the column/array
   */
  _updatePersonaData(cardData, column, index) {
    this.personaData = produce(this.personaData, draft => {
      draft[column][index] = cardData;
    });
    this._assetChange();
  }

  /**
   * Uses LIT repeat method to interate through the card objects within {@link #personaData} array located at the key passed in by the parameter column. Renders the correct card with data using a switch statement that uses the card object's cardType property as the case.
   * @param {String} column - the specific column to render the cards with data in
   * @returns {TemplateResult} card elements with data in their corresponding column
   */
  _renderCards(column) {
    return repeat(
      this.personaData[column],
      card => card?.title,
      (card, i) => {
        switch (card?.cardType) {
          case 'persona-card':
            return html`
              <persona-summary-card
                @persona-change=${e => {
                  this._updatePersonaData(e.detail.cardData, column, i);
                }}
                .summaryData=${card}
                .env=${this.env}
                .readOnly=${this.context.readonly}
              ></persona-summary-card>
            `;
          case 'text-card':
            return html`
              <persona-text-card
                @persona-change=${e => {
                  this._updatePersonaData(e.detail.cardData, column, i);
                }}
                .textData=${card}
                .readOnly=${this.context.readonly}
              ></persona-text-card>
            `;
          case 'word-cloud':
            return html`
              <persona-word-cloud
                @persona-change=${e => {
                  this._updatePersonaData(e.detail.cardData, column, i);
                }}
                .cloudData=${card}
                .readOnly=${this.context.readonly}
              >
              </persona-word-cloud>
            `;
          case 'empathy-persona':
            return html`
              <persona-empathy-card
                @persona-change=${e => {
                  this._updatePersonaData(e.detail.cardData, column, i);
                }}
                .empathyData=${card}
                .readOnly=${this.context.readonly}
              ></persona-empathy-card>
            `;
          default:
            return nothing;
        }
      },
    );
  }

  /**
   *
   * @returns {TemplateResult} main HTML to be rendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <div class="box">
          <div class="header-container">
            <div class="title-container">
              <h2 class="title">Create Persona Template</h2>
              <p>Click into each section to add or edit details</p>
            </div>
          </div>
          <div class="grid-container has-background-white">
            ${repeat(
              Object.keys(this.personaData),
              col => col,
              column => html`
                <div class="${column} grid-column">
                  ${this._renderCards(column)}
                </div>
              `,
            )}
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-audience-persona', CampaignAudiencePersona);
